<template>
  <div>
    <Card v-if="vehicleId === 'create'" icon="mdi-car-plus" title="Create Vehicle">
      <DynamicForm
        :key="vehicleId"
        :object="createVehicleObject"
        :fields="vehicleFields"
        :save-method="(data) => $api.rpc('vehicle', 'createVehicle', {vehicleHolderId: vehicleHolder.id, vehicle: data})"
        :success-callback="(item) => $router.push('/h/' + household.id + '/vehicle/' + vehicleHolder.id + '/' + item.id)"
        :focus-index="0"
      />
    </Card>

    <div v-else-if="vehicleId === 'settings' && vehicleHolder">
      <Card icon="mdi-car-multiple" :title="vehicleHolder.name">
        <EditableObject
          :object="vehicleHolder"
          :fields="[{fieldName:'name', displayName:'Name', type:String, editable:true, rules:[rules.required]}]"
          :saveMethod="(o) => $api.rpc('vehicle', 'saveVehicleHolder', {vehicleHolder: o})"
        />
        <br/>
        <ButtonWithConfirmation
          :confirmation-text='"Are you sure you want to delete \"" + vehicleHolder.name + "\"?"'
          confirmation-icon="mdi-delete"
          button-text="Delete"
          button-color="error"
          :action="() => $api.rpc('vehicle', 'deleteVehicleHolder', {vehicleHolder})"
          :success-callback="() => $router.push('/h/' + household.id)"
        />
      </Card>
      <Permissions :id="vehicleHolder.id" type="vehicleholder" showHeaderDivider/>
    </div>

    <div v-else-if="selectedVehicle">
      <Card icon="mdi-car" :title="selectedVehicle.name + (selectedVehicle.sold ? ' (Sold)' : '')" wide>
        <v-row dense class="mb-4">
          <v-col cols="12" md="4">
            <div class="summary-label">Vehicle</div>
            <div>{{ vehicleSubtitle(selectedVehicle) }}</div>
            <div class="vehicle-preview">Status: {{ selectedVehicle.sold ? 'Sold' : 'Active' }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="summary-label">Current Value</div>
            <div>{{ currentVehicleValue ? formatMoney(currentVehicleValue.value) + ' (' + currentVehicleValue.date + ')' : 'No value recorded' }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="summary-label">Current Odometer</div>
            <div>{{ currentOdometer != null ? formatInteger(currentOdometer) + ' mi' : 'No odometer entry yet' }}</div>
          </v-col>
        </v-row>
        <v-row dense class="mb-4">
          <v-col cols="12" md="3">
            <div class="summary-label">Next Maintenance</div>
            <div>{{ nextVehicleMaintenance ? nextVehicleMaintenance.option.name + ' - ' + dueSummary(nextVehicleMaintenance) : 'No maintenance options yet' }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="summary-label">Color</div>
            <div>{{ selectedVehicle.color || 'Not set' }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="summary-label">License Plate</div>
            <div>{{ selectedVehicle.licensePlate || 'Not set' }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="summary-label">VIN</div>
            <div>{{ selectedVehicle.vin || 'Not set' }}</div>
          </v-col>
        </v-row>
        <v-expansion-panels v-model="vehicleEditorPanel" flat class="vehicle-edit-panel">
          <v-expansion-panel>
            <v-expansion-panel-header>Edit Vehicle Details</v-expansion-panel-header>
            <v-expansion-panel-content>
              <DynamicForm
                :key="selectedVehicle.id + ':' + vehicleEditorPanel"
                :object="selectedVehicle"
                :fields="vehicleFields"
                :save-method="(o) => $api.rpc('vehicle', 'saveVehicle', {vehicle: o})"
                :delete-method="() => $api.rpc('vehicle', 'deleteVehicle', {vehicle: selectedVehicle})"
                :cancel-callback="() => { vehicleEditorPanel = null }"
                :success-callback="(response) => {
                  vehicleEditorPanel = null
                  if (!response)
                    $router.push('/h/' + household.id + '/vehicle/' + vehicleHolder.id)
                }"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </Card>

      <Card icon="mdi-chart-line" title="Value History" wide>
        <div v-if="hasValueChartData" class="vehicle-value-chart">
          <LineChart :data="valueChartData" :options="valueChartOptions"/>
        </div>
        <div v-else class="vehicle-chart-empty">
          No purchase, sale, or value history yet.
        </div>
      </Card>

      <Card icon="mdi-cash" title="Value" wide :actions="[{icon:'mdi-plus', color:'primary', click:() => openValueDialog()}]">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-currency-field v-model="todayValue" label="Today's Value" outlined dense prefix="$"/>
          </v-col>
          <v-col cols="12" md="4">
            <v-btn color="primary" :disabled="todayValue == null" @click="saveTodayValue">Save Today's Value</v-btn>
          </v-col>
        </v-row>
        <v-data-table :items="values" :headers="valueHeaders" hide-default-footer disable-pagination>
          <template v-slot:item.value="{ item }">{{ formatMoney(item.value) }}</template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openValueDialog(item)"><v-icon>mdi-pencil</v-icon></v-btn>
            <v-btn icon small @click="deleteValue(item)"><v-icon>mdi-delete</v-icon></v-btn>
          </template>
        </v-data-table>
      </Card>

      <Card icon="mdi-speedometer" title="Odometer" wide :actions="[{icon:'mdi-plus', color:'primary', click:() => openOdometerDialog()}]">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field v-model.number="todayOdometer" label="Today's Odometer" outlined dense type="number"/>
          </v-col>
          <v-col cols="12" md="4">
            <v-btn color="primary" :disabled="todayOdometer == null" @click="saveTodayOdometer">Save Today's Odometer</v-btn>
          </v-col>
        </v-row>
        <v-data-table :items="odometerEntries" :headers="odometerHeaders" hide-default-footer disable-pagination>
          <template v-slot:item.odometer="{ item }">{{ formatInteger(item.odometer) }} mi</template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openOdometerDialog(item)"><v-icon>mdi-pencil</v-icon></v-btn>
            <v-btn icon small @click="deleteOdometer(item)"><v-icon>mdi-delete</v-icon></v-btn>
          </template>
        </v-data-table>
      </Card>

      <Card icon="mdi-tools" title="Maintenance Options" wide :actions="[{icon:'mdi-plus', color:'primary', click:() => openMaintenanceOptionDialog()}]">
        <v-data-table :items="maintenanceOptions" :headers="maintenanceOptionHeaders" hide-default-footer disable-pagination>
          <template v-slot:item.name="{ item }">{{ item.option.name }}</template>
          <template v-slot:item.schedule="{ item }">{{ formatScheduleMode(item.option.scheduleMode) }}</template>
          <template v-slot:item.due="{ item }">{{ dueSummary(item) }}</template>
          <template v-slot:item.lastCompleted="{ item }">{{ lastDoneSummary(item) }}</template>
          <template v-slot:item.overdue="{ item }">
            <span :class="item.overdue ? 'error--text font-weight-bold' : 'success--text font-weight-bold'">{{ item.overdue ? 'Overdue' : 'OK' }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openMaintenanceOptionDialog(item.option)"><v-icon>mdi-pencil</v-icon></v-btn>
            <v-btn icon small @click="deleteMaintenanceOption(item.option)"><v-icon>mdi-delete</v-icon></v-btn>
          </template>
        </v-data-table>
      </Card>

      <Card icon="mdi-wrench" title="Maintenance History" wide :actions="[{icon:'mdi-plus', color:'primary', click:() => openMaintenanceEventDialog()}]">
        <v-data-table :items="maintenanceEvents" :headers="maintenanceEventHeaders" hide-default-footer disable-pagination>
          <template v-slot:item.options="{ item }">{{ maintenanceEventNames(item) }}</template>
          <template v-slot:item.odometer="{ item }">{{ item.odometer != null ? formatInteger(item.odometer) + ' mi' : '-' }}</template>
          <template v-slot:item.cost="{ item }">{{ item.cost ? formatMoney(item.cost) : '-' }}</template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openMaintenanceEventDialog(item)"><v-icon>mdi-pencil</v-icon></v-btn>
            <v-btn icon small @click="deleteMaintenanceEvent(item)"><v-icon>mdi-delete</v-icon></v-btn>
          </template>
        </v-data-table>
      </Card>
    </div>

    <Card v-else-if="vehicles && vehicles.length === 0" :title="'Welcome to ' + vehicleHolder.name">
      <span class="subtitle-1">
        Create your first vehicle by clicking
        <v-btn rounded color="primary" :to="'/h/' + household.id + '/vehicle/' + vehicleHolder.id + '/create'"><v-icon left>mdi-plus</v-icon>Create Vehicle</v-btn>
      </span>
    </Card>

    <div v-else>
      <Card v-for="vehicle in vehicles" :key="vehicle.id" icon="mdi-car" :title="vehicle.name + (vehicle.sold ? ' (Sold)' : '')" :to="'/h/' + household.id + '/vehicle/' + vehicleHolder.id + '/' + vehicle.id">
        <div>{{ vehicleSubtitle(vehicle) }}</div>
        <div class="vehicle-preview">Status: {{ vehicle.sold ? 'Sold' : 'Active' }}</div>
        <div class="vehicle-preview">Plate: {{ vehicle.licensePlate || 'Not set' }}</div>
      </Card>
    </div>

    <v-dialog v-model="showValueDialog" max-width="640">
      <Card title="Save Value" icon="mdi-cash" inDialog>
        <DynamicForm
          :key="showValueDialog + ':' + valueDialogObject.date"
          :object="valueDialogObject"
          :fields="valueDialogFields"
          :save-method="saveValueDialog"
          :cancel-callback="() => showValueDialog = false"
          :success-callback="() => { showValueDialog = false; loadVehicleData(); }"
          :focus-index="0"
        />
      </Card>
    </v-dialog>

    <v-dialog v-model="showOdometerDialog" max-width="720">
      <Card title="Odometer" icon="mdi-speedometer" inDialog>
        <DynamicForm
          :key="showOdometerDialog + ':' + odometerDialogObject.id"
          :object="odometerDialogObject"
          :fields="odometerDialogFields"
          :save-method="saveOdometerDialog"
          :cancel-callback="() => showOdometerDialog = false"
          :success-callback="() => { showOdometerDialog = false; loadVehicleData(); }"
          :focus-index="0"
        />
      </Card>
    </v-dialog>

    <v-dialog v-model="showMaintenanceOptionDialog" max-width="760">
      <Card title="Maintenance Option" icon="mdi-tools" inDialog>
        <DynamicForm
          :key="showMaintenanceOptionDialog + ':' + maintenanceOptionDialogObject.id"
          :object="maintenanceOptionDialogObject"
          :fields="maintenanceOptionDialogFields"
          :save-method="saveMaintenanceOptionDialog"
          :cancel-callback="() => showMaintenanceOptionDialog = false"
          :success-callback="() => { showMaintenanceOptionDialog = false; loadVehicleData(); }"
          @templateKeychange="applyVehicleTemplate"
          :focus-index="0"
        />
      </Card>
    </v-dialog>

    <v-dialog v-model="showMaintenanceEventDialog" max-width="760">
      <Card title="Maintenance" icon="mdi-wrench" inDialog>
        <v-text-field v-model="maintenanceEventForm.date" type="date" label="Date" outlined dense/>
        <v-text-field v-model.number="maintenanceEventForm.odometer" type="number" label="Odometer" outlined dense/>
        <v-currency-field v-model="maintenanceEventForm.cost" label="Cost" outlined dense prefix="$"/>
        <v-textarea v-model="maintenanceEventForm.notes" label="Notes" outlined dense/>
        <div class="subtitle-2 mb-2">Completed Maintenance</div>
        <v-checkbox
          v-for="option in maintenanceOptions"
          :key="option.option.id"
          v-model="maintenanceEventForm.vehicleMaintenanceOptionIds"
          :label="option.option.name"
          :value="option.option.id"
          hide-details
        />
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="showMaintenanceEventDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="saveMaintenanceEventDialog">Save</v-btn>
        </v-card-actions>
      </Card>
    </v-dialog>
  </div>
</template>

<script>
import Card from '@/components/Card'
import DynamicForm from '@/components/DynamicForm'
import EditableObject from '@/components/EditableObject'
import ButtonWithConfirmation from '@/components/ButtonWithConfirmation'
import Permissions from '@/components/Permissions'
import { MaintenanceScheduleMode } from '@/api'
import { Line as LineChart } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement } from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement)

export default {
  name: 'VehicleHolder',
  components: { Permissions, ButtonWithConfirmation, EditableObject, DynamicForm, Card, LineChart },
  props: ['household', 'vehicleHolderId', 'vehicleId'],
  data: () => ({
    subscription: null,
    vehicleHolder: null,
    vehicles: [],
    activeVehicles: [],
    maintenanceTemplates: [],
    maintenanceOptions: [],
    maintenanceEvents: [],
    odometerEntries: [],
    values: [],
    valueChartData: null,
    todayValue: null,
    todayOdometer: null,
    vehicleEditorPanel: null,
    showValueDialog: false,
    valueDialogObject: { date: '', value: null },
    showOdometerDialog: false,
    odometerDialogObject: {},
    showMaintenanceOptionDialog: false,
    maintenanceOptionDialogObject: {},
    showMaintenanceEventDialog: false,
    maintenanceEventForm: { id: null, date: '', odometer: null, cost: null, notes: '', vehicleMaintenanceOptionIds: [] },
    rules: {
      required: value => !!value || 'Required',
      max255: value => !value || value.length <= 255 || 'Must be 255 characters or less',
      year: value => {
        if (value == null || value === '')
          return true
        const year = Number(value)
        const maxYear = new Date().getFullYear() + 1
        return (Number.isInteger(year) && year >= 1886 && year <= maxYear) || ('Must be between 1886 and ' + maxYear)
      },
      nonNegative: value => value == null || Number(value) >= 0 || 'Cannot be negative',
      positiveInt: value => value == null || (Number.isInteger(Number(value)) && Number(value) > 0) || 'Must be a whole number greater than 0',
      vin: value => !value || (!value.includes(' ') && value.length <= 17) || 'VIN must be 17 characters or less and contain no spaces'
    },
    valueHeaders: [
      { text: 'Date', value: 'date' },
      { text: 'Value', value: 'value' },
      { text: '', value: 'actions', sortable: false }
    ],
    odometerHeaders: [
      { text: 'Date', value: 'date' },
      { text: 'Odometer', value: 'odometer' },
      { text: 'Notes', value: 'notes' },
      { text: '', value: 'actions', sortable: false }
    ],
    maintenanceOptionHeaders: [
      { text: 'Name', value: 'name' },
      { text: 'Schedule', value: 'schedule' },
      { text: 'Due', value: 'due' },
      { text: 'Last Done', value: 'lastCompleted' },
      { text: 'Status', value: 'overdue' },
      { text: '', value: 'actions', sortable: false }
    ],
    maintenanceEventHeaders: [
      { text: 'Date', value: 'date' },
      { text: 'Odometer', value: 'odometer' },
      { text: 'Completed', value: 'options' },
      { text: 'Cost', value: 'cost' },
      { text: '', value: 'actions', sortable: false }
    ]
  }),
  computed: {
    selectedVehicle () {
      return this.vehicles.find(vehicle => vehicle.id === this.vehicleId)
    },
    currentVehicleValue () {
      return this.values.length > 0 ? this.values[0] : null
    },
    currentOdometer () {
      return this.odometerEntries.length > 0 ? this.odometerEntries[0].odometer : null
    },
    todayOdometerEntry () {
      return this.odometerEntries.find(entry => entry.date === this.todayString()) || null
    },
    nextVehicleMaintenance () {
      if (!this.maintenanceOptions || this.maintenanceOptions.length === 0)
        return null
      return [...this.maintenanceOptions].sort((a, b) => {
        if (a.overdue && !b.overdue)
          return -1
        if (!a.overdue && b.overdue)
          return 1
        return this.dueSummary(a).localeCompare(this.dueSummary(b))
      })[0]
    },
    hasValueChartData () {
      return this.valueChartData && this.valueChartData.labels && this.valueChartData.labels.length > 0
    },
    valueChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: this.graphLabel } }
        },
        elements: {
          line: {
            borderColor: '#7bc4ff',
            borderWidth: 2,
            tension: 0.15
          },
          point: {
            radius: 4,
            hoverRadius: 5,
            backgroundColor: '#ffffff'
          }
        },
        scales: {
          y: {
            ticks: {
              callback: value => this.formatMoney(value)
            }
          }
        }
      }
    },
    vehicleFields () {
      return [
        { fieldName: 'name', displayName: 'Name', type: String, editable: true, rules: [this.rules.required, this.rules.max255], maxlength: 255 },
        { fieldName: 'description', displayName: 'Description', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'year', displayName: 'Year', type: Number, editable: true, rules: [this.rules.year] },
        { fieldName: 'make', displayName: 'Make', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'model', displayName: 'Model', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'trim', displayName: 'Trim', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'color', displayName: 'Color', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'vin', displayName: 'VIN', type: String, editable: true, rules: [this.rules.vin], maxlength: 17 },
        { fieldName: 'licensePlate', displayName: 'License Plate', type: String, editable: true, rules: [this.rules.max255], maxlength: 255 },
        { fieldName: 'sold', displayName: 'Sold', type: Boolean }
      ]
    },
    createVehicleObject () {
      return {
        name: '',
        description: '',
        year: null,
        make: '',
        model: '',
        trim: '',
        color: '',
        vin: '',
        licensePlate: '',
        sold: false
      }
    },
    valueDialogFields () {
      return [
        { fieldName: 'date', displayName: 'Date', type: 'LocalDate', small: true, rules: [this.rules.required] },
        { fieldName: 'value', displayName: 'Value', type: 'Currency', rules: [this.rules.required, this.rules.nonNegative] }
      ]
    },
    odometerDialogFields () {
      return [
        { fieldName: 'date', displayName: 'Date', type: 'LocalDate', small: true, rules: [this.rules.required] },
        { fieldName: 'odometer', displayName: 'Odometer', type: Number, rules: [this.rules.required, this.rules.nonNegative] },
        { fieldName: 'notes', displayName: 'Notes', type: 'LargeTextBox' }
      ]
    },
    maintenanceOptionDialogFields () {
      return [
        { fieldName: 'templateKey', displayName: 'Template', type: 'Enumerator', values: this.maintenanceTemplates.map(item => ({ text: item.name, value: item.key })), clearable: true },
        { fieldName: 'name', displayName: 'Name', type: String, rules: [this.rules.required, this.rules.max255], maxlength: 255 },
        { fieldName: 'scheduleMode', displayName: 'Schedule Mode', type: 'Enumerator', values: this.scheduleModes, rules: [this.rules.required] },
        { fieldName: 'dueStrategy', displayName: 'Due Strategy', type: 'Enumerator', values: this.dueStrategies, rules: [this.rules.required] },
        { fieldName: 'intervalMiles', displayName: 'Interval Miles', type: Number, rules: [this.rules.positiveInt], displayCondition: o => o.dueStrategy !== 'TIME_ONLY' },
        { fieldName: 'intervalMonths', displayName: 'Interval Months', type: Number, rules: [this.rules.positiveInt], displayCondition: o => o.dueStrategy !== 'MILES_ONLY' },
        { fieldName: 'startDate', displayName: 'Start Date', type: 'LocalDate', small: true, rules: [this.rules.required] },
        { fieldName: 'startOdometer', displayName: 'Start Odometer', type: Number, rules: [this.rules.nonNegative], displayCondition: o => o.dueStrategy !== 'TIME_ONLY' }
      ]
    },
    scheduleModes () {
      return [
        { text: 'Fixed Schedule', value: 'FIXED_SCHEDULE' },
        { text: 'Reset From Completion', value: 'RESET_FROM_COMPLETION' }
      ]
    },
    dueStrategies () {
      return [
        { text: 'Time Only', value: 'TIME_ONLY' },
        { text: 'Miles Only', value: 'MILES_ONLY' },
        { text: 'Whichever Comes First', value: 'WHICHEVER_COMES_FIRST' }
      ]
    }
  },
  methods: {
    resubscribe () {
      if (this.subscription)
        this.$api.unsubscribe(this.subscription)
      this.subscription = this.$api.subscribe('UIVehicleHolder', this.vehicleHolderId, holder => {
        this.vehicleHolder = holder.vehicleHolder
        this.vehicles = holder.vehicles || []
        this.activeVehicles = holder.activeVehicles || this.vehicles.filter(vehicle => !vehicle.sold)
        this.loadVehicleData()
        this.updateMenu()
        this.updateBreadcrumbs()
      })
    },
    updateMenu () {
      if (!this.vehicleHolder)
        return
      const menuItems = [{
        title: this.vehicleHolder.name,
        icon: 'mdi-car-multiple',
        to: '/h/' + this.household.id + '/vehicle/' + this.vehicleHolder.id,
        color: '#2F5D8C'
      }]
      this.activeVehicles.forEach(vehicle => {
        menuItems.push({ title: vehicle.name, icon: 'mdi-car', to: '/h/' + this.household.id + '/vehicle/' + this.vehicleHolder.id + '/' + vehicle.id })
      })
      menuItems.push({ title: 'Create Vehicle', icon: 'mdi-plus', to: '/h/' + this.household.id + '/vehicle/' + this.vehicleHolder.id + '/create' })
      menuItems.push({ title: 'Settings', icon: 'mdi-cog', to: '/h/' + this.household.id + '/vehicle/' + this.vehicleHolder.id + '/settings' })
      this.$emit('menuItems', menuItems)
    },
    updateBreadcrumbs () {
      if (!this.vehicleHolder)
        return
      const breadcrumbs = [{ text: this.vehicleHolder.name, icon: 'mdi-car-multiple', to: '/h/' + this.household.id + '/vehicle/' + this.vehicleHolder.id }]
      if (this.selectedVehicle)
        breadcrumbs.push({ text: this.selectedVehicle.name, icon: 'mdi-car', to: '/h/' + this.household.id + '/vehicle/' + this.vehicleHolder.id + '/' + this.selectedVehicle.id })
      this.$emit('breadcrumbs', breadcrumbs)
    },
    async loadVehicleData () {
      if (!this.selectedVehicle) {
        this.values = []
        this.valueChartData = null
        this.todayOdometer = null
        this.odometerEntries = []
        this.maintenanceOptions = []
        this.maintenanceEvents = []
        return
      }
      this.maintenanceTemplates = await this.$api.rpc('vehicle', 'getVehicleMaintenanceTemplates', { vehicleHolderId: this.vehicleHolder.id })
      this.values = await this.$api.rpc('vehicle', 'getVehicleValues', { vehicleHolderId: this.vehicleHolder.id, vehicleId: this.selectedVehicle.id })
      this.valueChartData = await this.$api.rpc('vehicle', 'getVehicleValueChartData', { vehicleHolderId: this.vehicleHolder.id, vehicleId: this.selectedVehicle.id })
      this.odometerEntries = await this.$api.rpc('vehicle', 'getVehicleOdometerEntries', { vehicleHolderId: this.vehicleHolder.id, vehicleId: this.selectedVehicle.id })
      this.maintenanceOptions = await this.$api.rpc('vehicle', 'getVehicleMaintenanceOptions', { vehicleHolderId: this.vehicleHolder.id, vehicleId: this.selectedVehicle.id })
      this.maintenanceEvents = await this.$api.rpc('vehicle', 'getVehicleMaintenanceEvents', { vehicleHolderId: this.vehicleHolder.id, vehicleId: this.selectedVehicle.id })
      this.todayOdometer = this.todayOdometerEntry ? this.todayOdometerEntry.odometer : null
    },
    formatMoney (value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0)
    },
    formatInteger (value) {
      return new Intl.NumberFormat('en-US').format(value || 0)
    },
    graphLabel (context) {
      let label = context.dataset.label || ''
      if (label)
        label += ': '
      if (context.parsed.y !== null)
        label += this.formatMoney(context.parsed.y)
      return label
    },
    vehicleSubtitle (vehicle) {
      const parts = [vehicle.year, vehicle.color, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean)
      return parts.length > 0 ? parts.join(' ') : 'No vehicle details yet'
    },
    todayString () {
      return new Date(new Date() - (new Date().getTimezoneOffset() * 60 * 1000)).toISOString().split('T')[0]
    },
    openValueDialog (value) {
      this.valueDialogObject = value ? { date: value.date, value: value.value } : { date: this.todayString(), value: null }
      this.showValueDialog = true
    },
    saveValueDialog (data) {
      return this.$api.rpc('vehicle', 'saveVehicleValue', { vehicleHolderId: this.vehicleHolder.id, vehicleId: this.selectedVehicle.id, date: data.date, value: data.value })
    },
    saveTodayValue () {
      return this.$api.rpc('vehicle', 'saveVehicleValue', { vehicleHolderId: this.vehicleHolder.id, vehicleId: this.selectedVehicle.id, date: this.todayString(), value: this.todayValue }).then(() => {
        this.todayValue = null
        this.loadVehicleData()
      })
    },
    deleteValue (value) {
      this.$api.rpc('vehicle', 'deleteVehicleValue', { vehicleHolderId: this.vehicleHolder.id, vehicleId: this.selectedVehicle.id, date: value.date }).then(() => this.loadVehicleData())
    },
    saveTodayOdometer () {
      const existingEntry = this.todayOdometerEntry
      const request = existingEntry
        ? this.$api.rpc('vehicle', 'saveVehicleOdometer', {
          vehicleHolderId: this.vehicleHolder.id,
          vehicleOdometer: { ...existingEntry, odometer: this.todayOdometer }
        })
        : this.$api.rpc('vehicle', 'createVehicleOdometer', {
          vehicleHolderId: this.vehicleHolder.id,
          vehicleId: this.selectedVehicle.id,
          date: this.todayString(),
          odometer: this.todayOdometer,
          notes: null
        })

      return request.then(() => {
        this.loadVehicleData()
      })
    },
    openOdometerDialog (entry) {
      this.odometerDialogObject = entry ? { ...entry } : { id: null, date: this.todayString(), odometer: null, notes: '' }
      this.showOdometerDialog = true
    },
    saveOdometerDialog (data) {
      if (data.id)
        return this.$api.rpc('vehicle', 'saveVehicleOdometer', { vehicleHolderId: this.vehicleHolder.id, vehicleOdometer: data })
      return this.$api.rpc('vehicle', 'createVehicleOdometer', { vehicleHolderId: this.vehicleHolder.id, vehicleId: this.selectedVehicle.id, date: data.date, odometer: data.odometer, notes: data.notes })
    },
    deleteOdometer (entry) {
      this.$api.rpc('vehicle', 'deleteVehicleOdometer', { vehicleHolderId: this.vehicleHolder.id, vehicleOdometer: entry }).then(() => this.loadVehicleData())
    },
    openMaintenanceOptionDialog (option) {
      this.maintenanceOptionDialogObject = option
        ? { ...option, templateKey: null }
        : { id: null, name: '', scheduleMode: 'RESET_FROM_COMPLETION', dueStrategy: 'WHICHEVER_COMES_FIRST', intervalMiles: 7500, intervalMonths: 12, startDate: this.todayString(), startOdometer: this.suggestedStartOdometer(), templateKey: null }
      this.showMaintenanceOptionDialog = true
    },
    applyVehicleTemplate (object) {
      const template = this.maintenanceTemplates.find(item => item.key === object.templateKey)
      if (!template)
        return
      object.name = template.name
      object.scheduleMode = template.scheduleMode
      object.dueStrategy = template.dueStrategy
      object.intervalMiles = template.intervalMiles
      object.intervalMonths = template.intervalMonths
      if (object.startOdometer == null)
        object.startOdometer = this.suggestedStartOdometer()
    },
    saveMaintenanceOptionDialog (data) {
      if (data.id) {
        const payload = { ...data }
        delete payload.templateKey
        return this.$api.rpc('vehicle', 'saveVehicleMaintenanceOption', { vehicleHolderId: this.vehicleHolder.id, option: payload })
      }
      return this.$api.rpc('vehicle', 'createVehicleMaintenanceOption', {
        vehicleHolderId: this.vehicleHolder.id,
        vehicleId: this.selectedVehicle.id,
        name: data.name,
        scheduleMode: data.scheduleMode,
        dueStrategy: data.dueStrategy,
        intervalMiles: data.intervalMiles,
        intervalMonths: data.intervalMonths,
        startDate: data.startDate,
        startOdometer: data.startOdometer
      })
    },
    deleteMaintenanceOption (option) {
      this.$api.rpc('vehicle', 'deleteVehicleMaintenanceOption', { vehicleHolderId: this.vehicleHolder.id, option }).then(() => this.loadVehicleData())
    },
    openMaintenanceEventDialog (event) {
      this.maintenanceEventForm = event
        ? { ...event, vehicleMaintenanceOptionIds: [...event.vehicleMaintenanceOptionIds] }
        : { id: null, date: this.todayString(), odometer: this.suggestedStartOdometer(), cost: null, notes: '', vehicleMaintenanceOptionIds: [] }
      this.showMaintenanceEventDialog = true
    },
    saveMaintenanceEventDialog () {
      const payload = { ...this.maintenanceEventForm }
      const call = payload.id
        ? this.$api.rpc('vehicle', 'saveVehicleMaintenanceEvent', { vehicleHolderId: this.vehicleHolder.id, event: payload })
        : this.$api.rpc('vehicle', 'createVehicleMaintenanceEvent', {
          vehicleHolderId: this.vehicleHolder.id,
          vehicleId: this.selectedVehicle.id,
          date: payload.date,
          odometer: payload.odometer,
          cost: payload.cost,
          notes: payload.notes,
          vehicleMaintenanceOptionIds: payload.vehicleMaintenanceOptionIds
        })
      call.then(() => {
        this.showMaintenanceEventDialog = false
        this.loadVehicleData()
      })
    },
    deleteMaintenanceEvent (event) {
      this.$api.rpc('vehicle', 'deleteVehicleMaintenanceEvent', { vehicleHolderId: this.vehicleHolder.id, event }).then(() => this.loadVehicleData())
    },
    formatScheduleMode (mode) {
      return MaintenanceScheduleMode[mode] || mode
    },
    dueSummary (item) {
      const parts = []
      if (item.nextDueDate)
        parts.push(item.nextDueDate)
      if (item.nextDueOdometer != null)
        parts.push(this.formatInteger(item.nextDueOdometer) + ' mi')
      return parts.join(' or ') || '-'
    },
    lastDoneSummary (item) {
      const parts = []
      if (item.lastCompletedDate)
        parts.push(item.lastCompletedDate)
      if (item.lastCompletedOdometer != null)
        parts.push(this.formatInteger(item.lastCompletedOdometer) + ' mi')
      return parts.join(' at ') || '-'
    },
    maintenanceEventNames (event) {
      return (event.vehicleMaintenanceOptionIds || []).map(optionId => {
        const option = this.maintenanceOptions.find(item => item.option.id === optionId)
        return option ? option.option.name : optionId
      }).join(', ')
    },
    suggestedStartOdometer () {
      return this.odometerEntries.length > 0 ? this.odometerEntries[0].odometer : null
    }
  },
  watch: {
    vehicleHolderId () {
      this.resubscribe()
    },
    vehicleId () {
      this.vehicleEditorPanel = null
      this.loadVehicleData()
      this.updateBreadcrumbs()
    }
  },
  mounted () {
    this.resubscribe()
  },
  destroyed () {
    if (this.subscription)
      this.$api.unsubscribe(this.subscription)
  }
}
</script>

<style scoped>
.summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
}

.vehicle-preview {
  color: rgba(255, 255, 255, 0.8);
}

.vehicle-edit-panel {
  margin-top: 12px;
}

.vehicle-value-chart {
  min-height: 280px;
}

.vehicle-chart-empty,
.vehicle-chart-caption {
  color: rgba(255, 255, 255, 0.7);
}

.vehicle-chart-caption {
  margin-top: 12px;
  font-size: 0.875rem;
}

</style>
